/* LOG — the daily entry surface: weight, tape, food.

   Three panels, each independently loaded and independently saveable, because
   the design's own rule is "never block manual entry on network state". A panel
   whose read failed still accepts typing, and a save that fails leaves every
   character where the user left it: the input is cleared only after the server
   has actually acknowledged the write.

   Until the auth PR lands `api.ts` sends no Authorization header, so every call
   here comes back 401 (or 503, if the server has no tokens configured). That is
   surfaced as a visible failure rather than a fake success. */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Pill, Window } from '../ds'
import {
  errorMessage,
  getHistory,
  getNutrition,
  postLog,
  postNutrition,
  type HistoryPoint,
  type Meal,
  type MeasurementIn,
  type Metric,
  type NutritionEntry,
  type NutritionIn,
} from '../api'
import { headingDate, monoDate } from './format'
import { staleLabel, useShellSync } from '../app/syncOnLoad'

/* ── Shape of the form ───────────────────────────────────────────────────── */

type TapeMetric = Exclude<Metric, 'weight'>

const TAPE_FIELDS: readonly { metric: TapeMetric; label: string; placeholder: string }[] = [
  { metric: 'waist', label: 'WAIST', placeholder: '32.0' },
  { metric: 'hips', label: 'HIPS', placeholder: '40.5' },
  { metric: 'chest', label: 'CHEST', placeholder: '37.5' },
  { metric: 'arms', label: 'ARMS', placeholder: '12.0' },
  { metric: 'thighs', label: 'THIGHS', placeholder: '22.5' },
]

/** The four macros the form writes. `fiber_g` exists on the API but not here. */
type MacroKey = 'calories' | 'protein_g' | 'carbs_g' | 'fat_g'

const MACRO_FIELDS: readonly { key: MacroKey; label: string; placeholder: string }[] = [
  { key: 'calories', label: 'KCAL', placeholder: '450' },
  { key: 'protein_g', label: 'PROTEIN', placeholder: '30' },
  { key: 'carbs_g', label: 'CARBS', placeholder: '45' },
  { key: 'fat_g', label: 'FAT', placeholder: '15' },
]

const MEALS: readonly Meal[] = ['breakfast', 'lunch', 'dinner', 'snack', 'daily_total']

type TapeInputs = Partial<Record<TapeMetric, string>>
type MacroInputs = Record<MacroKey, string>

const EMPTY_MACROS: MacroInputs = { calories: '', protein_g: '', carbs_g: '', fat_g: '' }

/* ── Loading ─────────────────────────────────────────────────────────────── */

type Load<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'error'; message: string }

type TapeLatest = Partial<Record<TapeMetric, HistoryPoint>>

/** How far back to look for the most recent reading. Tape gets measured rarely. */
const LOOKBACK_DAYS = 365

/** Confirmation flash lifetimes, per the design spec. */
const WEIGHT_FLASH_MS = 2800
const TAPE_FLASH_MS = 2400

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError'
}

/** Latest point in a history series, or null when the series is empty. */
function latest(points: readonly HistoryPoint[]): HistoryPoint | null {
  return points.at(-1) ?? null
}

/** A positive finite number from a form field, or null if it is blank/unusable. */
function parseField(raw: string | undefined): number | null {
  const trimmed = raw?.trim()
  if (!trimmed) return null
  const value = Number.parseFloat(trimmed)
  return Number.isFinite(value) && value > 0 ? value : null
}

/** BREAKFAST → BREAK, DINNER → DINNE. The tag box fits five characters. */
function mealTag(meal: Meal): string {
  return meal === 'daily_total' ? 'TOTAL' : meal.toUpperCase().slice(0, 5)
}

function mealLabel(meal: Meal): string {
  return meal === 'daily_total' ? 'DAILY TOTAL' : meal.toUpperCase()
}

/* ── Totals ──────────────────────────────────────────────────────────────── */

/** The four numbers under TOTAL. Short keys because that line is the only reader. */
interface Totals {
  cal: number
  p: number
  c: number
  f: number
}

/* What TODAY SO FAR adds up to.

   A `daily_total` row **replaces** that date's individual meal rows; it does not
   add to them. That is Milo's domain rule, not a display choice here — the
   authoritative implementation is `macro_weekly_averages` in
   `src/milo/queries.py`, and it exists because the two kinds of row are two
   accounts of the same food. Someone who logs three meals and then a
   "2100 today" from a tracker app has eaten 2100 calories, not 4200. Summing
   every row, as this used to, double-counts exactly the days that were logged
   most carefully.

   Grouped by date rather than applied globally, because the payload is not
   guaranteed to be one day: `days=0` is a lower bound only (see `loadFood`), so
   a future-dated entry can ride along. Per date the rule is local — one day's
   `daily_total` must not suppress another day's meals — and the days are then
   summed.

   **This is the second client-side mirror of that rule; `Macros.tsx`'s `dayTotals`
   is the first, and this one must agree with it exactly.** Not an import of it:
   pulling a function out of a screen module drags that screen's component tree
   and its stylesheet into this one's graph for the sake of four additions. The
   guarantee instead comes from `Log.totals.test.ts`, which runs both functions
   over the same day and asserts they match — so a divergence fails a test rather
   than showing a user two answers for one day.

   Hence the arithmetic here is MACROS' arithmetic, deliberately: summed raw and
   rounded once at the end, not rounded per row. `calories` is `Numeric(7,1)` and
   the wire carries the float, so two 100.4 kcal meals are 200.8 → 201. Rounding
   each row first would give 200 — and MACROS would still say 201 for that same
   day. A penny of disagreement between the TOTAL line and the KCAL column above
   it is a much smaller problem than two screens disagreeing about a day. */
export function dayTotals(entries: readonly NutritionEntry[]): Totals {
  const totalByDate = new Map<string, NutritionEntry>()
  const mealsByDate = new Map<string, NutritionEntry[]>()

  for (const entry of entries) {
    if (entry.meal === 'daily_total') {
      // Last one wins if the server ever hands back two for a date; it upserts
      // on (user, date, meal, source), so this is only reachable across sources.
      totalByDate.set(entry.date, entry)
    } else {
      mealsByDate.get(entry.date)?.push(entry) ?? mealsByDate.set(entry.date, [entry])
    }
  }

  const counted: NutritionEntry[] = [...totalByDate.values()]
  for (const [date, meals] of mealsByDate) {
    if (!totalByDate.has(date)) counted.push(...meals)
  }

  let cal = 0
  let p = 0
  let c = 0
  let f = 0
  for (const row of counted) {
    cal += row.calories ?? 0
    p += row.protein_g ?? 0
    c += row.carbs_g ?? 0
    f += row.fat_g ?? 0
  }
  return { cal: Math.round(cal), p: Math.round(p), c: Math.round(c), f: Math.round(f) }
}

/* ── Screen ──────────────────────────────────────────────────────────────── */

/** Window shell + body, so two windows in one grid row stretch to equal height. */
const FILL = {
  style: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  bodyStyle: { flex: 1 },
} as const

export function Log() {
  /* The app-shell FatSecret sync (issue #88). `epoch` re-runs the reads below
     when a sync the shell watched has finished — form state never keys off it,
     so a refresh cannot touch what was typed. `sync` feeds at most one pill. */
  const { epoch, sync } = useShellSync()

  // Reads.
  const [weightLoad, setWeightLoad] = useState<Load<HistoryPoint | null>>({ status: 'loading' })
  const [tapeLoad, setTapeLoad] = useState<Load<TapeLatest>>({ status: 'loading' })
  const [foodLoad, setFoodLoad] = useState<Load<NutritionEntry[]>>({ status: 'loading' })

  // Form values. Never cleared by anything but a confirmed save.
  const [weightIn, setWeightIn] = useState('')
  const [tapeIn, setTapeIn] = useState<TapeInputs>({})
  const [meal, setMeal] = useState<Meal>('breakfast')
  const [foodDesc, setFoodDesc] = useState('')
  const [macros, setMacros] = useState<MacroInputs>(EMPTY_MACROS)

  // Writes.
  const [savingWeight, setSavingWeight] = useState(false)
  const [savingTape, setSavingTape] = useState(false)
  const [savingFood, setSavingFood] = useState(false)
  const [weightError, setWeightError] = useState<string | null>(null)
  const [tapeError, setTapeError] = useState<string | null>(null)
  const [foodError, setFoodError] = useState<string | null>(null)

  // Confirmations.
  const [savedWeight, setSavedWeight] = useState<string | null>(null)
  const [savedTape, setSavedTape] = useState(false)
  const weightTimer = useRef<number | undefined>(undefined)
  const tapeTimer = useRef<number | undefined>(undefined)

  /* The in-flight guard, held in a ref rather than read off the `saving*` state.

     `saving*` is state, and a save reads it from the closure of the render it
     was created in. React normally commits a discrete event's update before the
     next event runs, which is why the old `if (savingWeight) return` usually
     held — but not always: with a held Enter key and a slow enough request, two
     keydowns can land in one task, both read `false`, and two POSTs go out. That
     was reproduced against a local backend with a 1.5s round trip. The writes
     upsert, so the duplicate is harmless data-wise; it is still a request that
     should never have been made, and a ref flips synchronously so it cannot be.

     The inputs stay enabled throughout: refusing a *duplicate* request is not
     the same as blocking entry, and nothing typed is touched either way. */
  const inFlight = useRef({ weight: false, tape: false, food: false })

  const loadWeight = useCallback(async (signal?: AbortSignal) => {
    setWeightLoad({ status: 'loading' })
    try {
      const res = await getHistory({ metric: 'weight', days: LOOKBACK_DAYS }, signal)
      setWeightLoad({ status: 'ready', data: latest(res.points) })
    } catch (error) {
      if (isAbort(error)) return
      setWeightLoad({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadTape = useCallback(async (signal?: AbortSignal) => {
    setTapeLoad({ status: 'loading' })
    try {
      // Five metrics, five series — /history answers for one metric at a time.
      const series = await Promise.all(
        TAPE_FIELDS.map((f) => getHistory({ metric: f.metric, days: LOOKBACK_DAYS }, signal)),
      )
      const data: TapeLatest = {}
      TAPE_FIELDS.forEach((f, i) => {
        const point = latest(series[i].points)
        if (point) data[f.metric] = point
      })
      setTapeLoad({ status: 'ready', data })
    } catch (error) {
      if (isAbort(error)) return
      setTapeLoad({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  const loadFood = useCallback(async (signal?: AbortSignal) => {
    setFoodLoad({ status: 'loading' })
    try {
      // days=0 means "since the server's today" — the day boundary is drawn by
      // MILO_TIMEZONE, not by whatever clock this browser happens to have.
      //
      // It is a lower bound only: `days` mode leaves the upper edge open, so an
      // entry written with an explicit future date (only the MCP tools can do
      // that; this screen never sends one) would land in TODAY SO FAR. Bounding
      // it from here would mean sending from/to computed off the browser clock,
      // trading a rare, self-inflicted over-count for a routine wrong-day read.
      // The upper bound belongs where today is actually known: the server.
      const res = await getNutrition({ days: 0 }, signal)
      setFoodLoad({ status: 'ready', data: res.entries })
    } catch (error) {
      if (isAbort(error)) return
      setFoodLoad({ status: 'error', message: errorMessage(error) })
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void loadWeight(controller.signal)
    void loadTape(controller.signal)
    void loadFood(controller.signal)
    return () => controller.abort()
    // `epoch` is the shell sync's refresh signal: a bump means a FatSecret run
    // finished after these reads went out, so run them again.
  }, [loadWeight, loadTape, loadFood, epoch])

  useEffect(
    () => () => {
      window.clearTimeout(weightTimer.current)
      window.clearTimeout(tapeTimer.current)
    },
    [],
  )

  /* ── Saves ─────────────────────────────────────────────────────────────── */

  async function saveWeight() {
    if (inFlight.current.weight) return

    const value = parseField(weightIn)
    if (value === null) {
      // Say so, rather than returning into silence: an unexplained no-op reads
      // as a broken button, and the user cannot tell a rejected value from a
      // failed save. The field keeps whatever they typed.
      setWeightError(weightIn.trim() ? 'Use a number above zero.' : 'Enter a weight first.')
      return
    }

    inFlight.current.weight = true
    setSavingWeight(true)
    setWeightError(null)
    try {
      await postLog({ metric: 'weight', value, unit: 'lb' })
      setWeightIn('')
      setSavedWeight(`Logged ${value.toFixed(1)} lb for today. The chain lives.`)
      window.clearTimeout(weightTimer.current)
      weightTimer.current = window.setTimeout(() => setSavedWeight(null), WEIGHT_FLASH_MS)
      void loadWeight()
    } catch (error) {
      // The typed value stays exactly where it is.
      setWeightError(errorMessage(error))
    } finally {
      inFlight.current.weight = false
      setSavingWeight(false)
    }
  }

  async function saveTape() {
    if (inFlight.current.tape) return

    // Only what was measured — blank fields are not written, so a skipped
    // measurement never becomes a fabricated one. A field that was filled in
    // but cannot be read is different: it stops the whole batch rather than
    // being dropped, because a partial save clears the inputs and would take
    // the unreadable value down with it.
    const rows: MeasurementIn[] = []
    for (const field of TAPE_FIELDS) {
      const raw = tapeIn[field.metric]?.trim()
      if (!raw) continue
      const value = parseField(raw)
      if (value === null) {
        setTapeError(`${field.label}: use a number above zero.`)
        return
      }
      rows.push({ metric: field.metric, value, unit: 'in' })
    }
    if (rows.length === 0) {
      setTapeError('Enter at least one measurement.')
      return
    }

    inFlight.current.tape = true
    setSavingTape(true)
    setTapeError(null)
    try {
      // One array, one request, one transaction.
      await postLog(rows)
      setTapeIn({})
      setSavedTape(true)
      window.clearTimeout(tapeTimer.current)
      tapeTimer.current = window.setTimeout(() => setSavedTape(false), TAPE_FLASH_MS)
      void loadTape()
    } catch (error) {
      setTapeError(errorMessage(error))
    } finally {
      inFlight.current.tape = false
      setSavingTape(false)
    }
  }

  async function saveFood() {
    if (inFlight.current.food) return

    const description = foodDesc.trim()
    const entry: NutritionIn = { meal }
    if (description) entry.description = description
    // As with tape: blank macros are simply not sent, but a filled-in one that
    // cannot be read stops the save instead of vanishing from it.
    for (const field of MACRO_FIELDS) {
      const raw = macros[field.key].trim()
      if (!raw) continue
      const value = parseField(raw)
      if (value === null) {
        setFoodError(`${field.label}: use a number above zero.`)
        return
      }
      entry[field.key] = Math.round(value)
    }
    if (!description && entry.calories === undefined) {
      setFoodError('Say what you ate, or give it a calorie count.')
      return
    }

    inFlight.current.food = true
    setSavingFood(true)
    setFoodError(null)
    try {
      await postNutrition(entry)
      setFoodDesc('')
      setMacros(EMPTY_MACROS)
      // Re-read rather than appending locally: /nutrition upserts on
      // (user, date, meal, source), so a second breakfast replaces the first and
      // an optimistic append would show a row the server does not have.
      void loadFood()
    } catch (error) {
      setFoodError(errorMessage(error))
    } finally {
      inFlight.current.food = false
      setSavingFood(false)
    }
  }

  /* ── Derived ───────────────────────────────────────────────────────────── */

  // /nutrition orders by (date, meal), which is alphabetical — DINNER lands
  // above LUNCH. Re-order into mealtime order, the same order as the chip row.
  const entries =
    foodLoad.status === 'ready'
      ? [...foodLoad.data].sort((a, b) => MEALS.indexOf(a.meal) - MEALS.indexOf(b.meal))
      : []
  // Every row still renders below, `daily_total` included — it is only the
  // TOTAL line that treats one as standing in for the day's meals.
  const totals = dayTotals(entries)

  let lastWeightLabel: string
  if (weightLoad.status === 'loading') lastWeightLabel = 'LOADING…'
  else if (weightLoad.status === 'error') lastWeightLabel = 'UNAVAILABLE'
  else if (weightLoad.data === null) lastWeightLabel = 'NOTHING LOGGED YET'
  else {
    const unit = (weightLoad.data.unit ?? 'lb').toUpperCase()
    lastWeightLabel = `${weightLoad.data.value.toFixed(1)} ${unit} / ${monoDate(weightLoad.data.date)}`
  }

  const loadError =
    weightLoad.status === 'error'
      ? weightLoad.message
      : tapeLoad.status === 'error'
        ? tapeLoad.message
        : foodLoad.status === 'error'
          ? foodLoad.message
          : null

  /* Non-null only when the shell sync left this screen on cached FatSecret
     data with an age to quote. `new Date()` formats that timestamp for
     display; it decides nothing about which day any data belongs to. */
  const stale = staleLabel(sync, new Date())

  /* ── Render ────────────────────────────────────────────────────────────── */

  return (
    <>
      <div className="milo-heading-row">
        <h2 className="milo-heading">Log / {headingDate()}</h2>
        {/* Paper, not a danger strip: stale is a caveat about freshness, and
            the cached rows below are still right about what they say. */}
        {stale ? <Pill color="paper">{stale}</Pill> : null}
        <Pill color="pink">Manual beats synced</Pill>
      </div>

      {/* One strip at a time, most urgent first: a failed save, then a
          confirmed one, then the standing "reads are not working" notice. */}
      {weightError ? (
        <Alert tone="danger" role="alert" title="Not saved.">
          {weightError}
        </Alert>
      ) : savedWeight ? (
        <Alert tone="good" title="Saved.">
          {savedWeight}
        </Alert>
      ) : loadError ? (
        <Alert tone="danger" role="alert" title="Can’t read your log.">
          {loadError} Entry still works — what you type is kept.
        </Alert>
      ) : null}

      <div className="milo-grid">
        <Window title="WEIGHT.LOG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel">
            <div className="milo-weight-row">
              <input
                id="milo-weight"
                className="milo-weight-input"
                type="number"
                inputMode="decimal"
                step="0.1"
                min="0"
                placeholder="154.0"
                aria-label="Today’s weight in pounds"
                value={weightIn}
                onChange={(e) => setWeightIn(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void saveWeight()
                  }
                }}
              />
              <span className="milo-weight-suffix" aria-hidden="true">
                LB
              </span>
            </div>
            <Button
              variant="primary"
              block
              iconRight="↗"
              disabled={savingWeight}
              aria-busy={savingWeight}
              onClick={() => void saveWeight()}
            >
              Save weight
            </Button>
            <div className="milo-note">LAST: {lastWeightLabel}</div>
          </div>
        </Window>

        <Window title="TAPE.LOG" shadow="m" style={FILL.style} bodyStyle={FILL.bodyStyle}>
          <div className="milo-panel">
            <div className="milo-note milo-note--sm">ONLY WHAT YOU MEASURED / INCHES</div>
            <div className="milo-field-grid">
              {TAPE_FIELDS.map((field) => {
                // The last reading is the ghost, not the value: the panel's own
                // rule is that only what you measured today gets written.
                const last =
                  tapeLoad.status === 'ready' ? tapeLoad.data[field.metric] : undefined
                return (
                  <label className="milo-field" key={field.metric}>
                    <span className="milo-field-label">{field.label}</span>
                    <input
                      className="milo-field-input"
                      type="number"
                      inputMode="decimal"
                      step="0.1"
                      min="0"
                      placeholder={last ? last.value.toFixed(1) : field.placeholder}
                      value={tapeIn[field.metric] ?? ''}
                      onChange={(e) =>
                        setTapeIn((prev) => ({ ...prev, [field.metric]: e.target.value }))
                      }
                    />
                  </label>
                )
              })}
            </div>
            <Button
              variant="dark"
              block
              iconRight="↗"
              disabled={savingTape}
              aria-busy={savingTape}
              onClick={() => void saveTape()}
            >
              Save tape
            </Button>
            {tapeError ? (
              <div className="milo-flash milo-flash--bad" role="alert">
                ✕ {tapeError}
              </div>
            ) : savedTape ? (
              <div className="milo-flash" role="status">
                ✓ TAPE SAVED FOR TODAY
              </div>
            ) : null}
          </div>
        </Window>
      </div>

      <Window title="FOOD.LOG" shadow="m">
        <div className="milo-panel">
          <div className="milo-chips" role="group" aria-label="Meal">
            {MEALS.map((m) => (
              <button
                key={m}
                type="button"
                className="milo-chip"
                aria-pressed={m === meal}
                onClick={() => setMeal(m)}
              >
                {mealLabel(m)}
              </button>
            ))}
          </div>

          <input
            className="milo-text-input"
            type="text"
            placeholder="What did you eat? Rough is fine."
            aria-label="What did you eat?"
            value={foodDesc}
            onChange={(e) => setFoodDesc(e.target.value)}
          />

          <div className="milo-macro-grid">
            {MACRO_FIELDS.map((field) => (
              <label className="milo-field" key={field.key}>
                <span className="milo-field-label">{field.label}</span>
                <input
                  className="milo-field-input milo-macro-input"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  placeholder={field.placeholder}
                  value={macros[field.key]}
                  onChange={(e) =>
                    setMacros((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                />
              </label>
            ))}
          </div>

          <Button
            variant="primary"
            iconRight="＋"
            disabled={savingFood}
            aria-busy={savingFood}
            onClick={() => void saveFood()}
          >
            Log food
          </Button>

          {foodError ? (
            <div className="milo-flash milo-flash--bad" role="alert">
              ✕ {foodError}
            </div>
          ) : null}

          <div className="milo-today">
            <div className="milo-note milo-note--sm milo-today-head">TODAY SO FAR</div>

            {foodLoad.status === 'loading' ? (
              <div className="milo-note milo-note--sm">LOADING…</div>
            ) : foodLoad.status === 'error' ? (
              <div className="milo-note milo-note--sm">COULD NOT LOAD TODAY’S ENTRIES</div>
            ) : entries.length === 0 ? (
              <div className="milo-note milo-note--sm">NOTHING LOGGED YET</div>
            ) : (
              entries.map((entry) => (
                <div className="milo-entry" key={entry.id}>
                  <span className="milo-entry-tag">{mealTag(entry.meal)}</span>
                  <span className="milo-entry-desc">
                    {entry.description || '(no description)'}
                  </span>
                  <span className="milo-entry-cal">
                    {Math.round(entry.calories ?? 0)} KCAL
                  </span>
                </div>
              ))
            )}

            {/* Only claim a total once the day has actually been read. */}
            {foodLoad.status === 'ready' ? (
              <div className="milo-total">
                <span className="milo-total-label">TOTAL</span>
                <span className="milo-total-value">
                  {totals.cal} KCAL ✦ P{totals.p} C{totals.c} F{totals.f}
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </Window>
    </>
  )
}

export default Log
